import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Componente que detecta y notifica al usuario cuando hay una nueva versión
 * de la aplicación disponible (Service Worker actualizado)
 */
export default function ServiceWorkerUpdater() {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Solo ejecutar si el navegador soporta Service Workers
    if ('serviceWorker' in navigator) {
      // Verificar si hay actualizaciones cada 60 segundos
      const interval = setInterval(() => {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) {
            reg.update();
          }
        });
      }, 60000); // Cada minuto

      // Detectar cuando hay un Service Worker esperando para activarse
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          setRegistration(reg);
          
          // Si ya hay un Service Worker esperando, mostrar banner
          if (reg.waiting) {
            setShowUpdateBanner(true);
          }

          // Escuchar por nuevas actualizaciones
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Hay una nueva versión disponible
                setShowUpdateBanner(true);
                toast.info('Nueva versión disponible', {
                  description: 'Hay una actualización de la aplicación',
                  duration: 5000
                });
              }
            });
          });
        }
      });

      return () => clearInterval(interval);
    }
  }, []);

  const handleUpdate = () => {
    if (registration && registration.waiting) {
      // Enviar mensaje al Service Worker para que se active inmediatamente
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Escuchar cuando el nuevo Service Worker tome control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Recargar la página para usar la nueva versión
        window.location.reload();
      });
      
      toast.success('Actualizando...', { duration: 2000 });
    }
  };

  if (!showUpdateBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-emerald-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1">
            <span className="flex p-2 rounded-lg bg-emerald-700">
              <RefreshCw className="h-6 w-6 text-white animate-spin" />
            </span>
            <p className="ml-3 font-medium">
              <span className="md:hidden">Nueva versión disponible</span>
              <span className="hidden md:inline">
                Hay una nueva versión de la aplicación disponible con mejoras y correcciones
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-2 mt-2 sm:mt-0">
            <Button
              onClick={handleUpdate}
              size="sm"
              className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              Actualizar Ahora
            </Button>
            <button
              onClick={() => setShowUpdateBanner(false)}
              className="p-2 rounded-md hover:bg-emerald-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
